import { useEffect, useState } from 'react';
import { totp, rodo, auth as authApi, notificationPrefs,
         type NotificationPrefDto } from '@/api/endpoints';
import { useAuth } from '@/auth/AuthContext';
import { Icon } from '@/components/Icon';
import { McpTokensCard } from '@/components/McpTokensCard';
import { McpOAuthCard } from '@/components/McpOAuthCard';

export function Settings() {
  const { user } = useAuth();
  const [status, setStatus] = useState<{ enabled: boolean; wymagane: boolean; backupCodesCount: number } | null>(null);
  const [enroll, setEnroll] = useState<{ secret: string; qrImage: string; backupCodes: string[] } | null>(null);
  const [code, setCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => { void loadStatus(); }, []);

  async function loadStatus() {
    try {
      const s = await totp.status();
      setStatus(s);
    } catch (e) { setErr(String(e)); }
  }

  async function startEnroll() {
    setErr(null); setMsg(null);
    try {
      const r = await totp.startEnroll();
      setEnroll(r);
    } catch (e) { setErr(String(e)); }
  }

  async function confirm() {
    if (!enroll) return;
    setErr(null); setMsg(null);
    try {
      const r = await totp.confirmEnroll(enroll.secret, code, enroll.backupCodes);
      if (r.ok) {
        setMsg('2FA włączone. Zachowaj kody zapasowe w bezpiecznym miejscu.');
        setEnroll(null); setCode('');
        await loadStatus();
      }
    } catch (e: any) {
      setErr(e.message ?? 'Błędny code');
    }
  }

  async function disable() {
    setErr(null);
    try {
      await totp.disable(disableCode);
      setMsg('2FA wyłączone');
      setDisableCode('');
      await loadStatus();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Nie udało się wyłączyć 2FA');
    }
  }

  async function exportMyData() {
    if (!user) return;
    try {
      const data = await rodo.eksportUser(user.id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `rodo-eksport-${user.login}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMsg('Data wyeksportowane (art. 15 RODO)');
    } catch (e: any) { setErr(e.message ?? String(e)); }
  }

  return (
    <div className="max-w-[900px] mx-auto">
      <header className="mb-6">
        <h1>Ustawienia konta</h1>
        <div className="text-ink-400 text-[13px]">Zarządzanie 2FA, hasłem, zdalnymi połączeniami MCP, powiadomieniami, RODO</div>
      </header>

      {msg && <div className="mb-4 p-3 bg-green-100 text-green-500 rounded-md text-[13px]">{msg}</div>}
      {err && <div className="mb-4 p-3 bg-red-100 text-red-500 rounded-md text-[13px]">{err}</div>}

      <div className="card mb-6">
        <div className="card-head">
          <div className="card-title">Dwuskładnikowe uwierzytelnianie (2FA)</div>
          {status?.enabled && <span className="pill pill-green ml-auto">Włączone</span>}
          {status?.wymagane && !status?.enabled && <span className="pill pill-red ml-auto">Wymagane dla Twojej roli</span>}
        </div>
        <div className="card-body">
          {!status && <div className="text-ink-400">Ładowanie…</div>}
          {status && !status.enabled && !enroll && (
            <>
              <p className="text-ink-600 text-[13px] mb-3">
                Skonfiguruj code TOTP (Google Authenticator, Authy, 1Password, FreeOTP).
                {status.wymagane && <strong className="text-red-500"> Twoja role wymaga 2FA do podpisywania dokumentacji.</strong>}
              </p>
              <button className="btn btn-water btn-sm" onClick={() => void startEnroll()}>
                <Icon name="shield" /> Włącz 2FA
              </button>
            </>
          )}
          {enroll && (
            <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5">
              <div>
                <img src={enroll.qrImage} alt="QR code TOTP" className="w-48 h-48 border border-ink-100 rounded" />
                <div className="mt-2 text-[11px] text-ink-400 font-mono">{enroll.secret}</div>
              </div>
              <div>
                <ol className="text-[13px] list-decimal list-inside text-ink-700 space-y-1 mb-3">
                  <li>Zeskanuj QR code w aplikacji 2FA</li>
                  <li>Wpisz poniżej 6-cyfrowy code z aplikacji</li>
                  <li>Zapisz kody zapasowe w bezpiecznym miejscu</li>
                </ol>
                <input
                  className="input max-w-[200px] font-mono text-center text-[18px]"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                />
                <button className="btn btn-primary btn-sm ml-2" onClick={() => void confirm()} disabled={code.length !== 6}>
                  Potwierdź
                </button>

                <div className="mt-4 p-3 bg-amber-100 rounded-md">
                  <div className="text-[12px] font-semibold text-amber-500 mb-1">Codey zapasowe (użyj gdy utracisz dostęp do aplikacji)</div>
                  <div className="grid grid-cols-2 gap-1 font-mono text-[12px]">
                    {enroll.backupCodes.map(c => <div key={c}>{c}</div>)}
                  </div>
                </div>
              </div>
            </div>
          )}
          {status?.enabled && (
            <>
              <p className="text-ink-600 text-[13px] mb-3">
                2FA jest aktywne. Pozostało kodów zapasowych: <strong>{status.backupCodesCount}</strong>.
              </p>
              {status.wymagane ? (
                <p className="text-[13px] text-ink-500 bg-ink-50 border border-ink-200 rounded p-2.5">
                  Twoja rola wymaga logowania dwuskładnikowego, więc 2FA nie można wyłączyć.
                  Aby zmienić aplikację uwierzytelniającą, przejdź konfigurację od nowa.
                </p>
              ) : (
                <div className="flex items-center gap-2">
                  <input value={disableCode} onChange={e => setDisableCode(e.target.value.trim())}
                         inputMode="numeric" autoComplete="one-time-code" maxLength={12}
                         placeholder="Kod z aplikacji lub zapasowy" className="input w-56" />
                  <button className="btn btn-secondary btn-sm" disabled={disableCode.length < 6}
                          onClick={() => void disable()}>Wyłącz 2FA</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ChangePasswordCard />

      <McpOAuthCard />
      <McpTokensCard />

      <div className="card mb-6">
        <div className="card-head">
          <div className="card-title">Moje dane (RODO)</div>
        </div>
        <div className="card-body">
          <p className="text-ink-600 text-[13px] mb-3">
            Zgodnie z art. 15 RODO masz prawo do wglądu i kopii wszystkich swoich danych osobowych przetwarzanych przez Hydro-Wolski.
          </p>
          <button className="btn btn-secondary btn-sm" onClick={() => void exportMyData()}>
            <Icon name="download" /> Eksportuj moje dane (JSON)
          </button>
        </div>
      </div>

      <ProfileEditCard/>

      <NotificationPrefsCard/>

      <div className="card">
        <div className="card-head">
          <div className="card-title">Konto (tylko do wglądu)</div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-2 gap-4 text-[13px]">
            <div><div className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">Login</div><div className="font-mono">{user?.login}</div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">Role</div><div>{user?.roles?.join(', ') ?? '—'}</div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">Kategoria uprawnień</div><div>{user?.category}</div></div>
          </div>
          <div className="mt-3 text-[11px] text-ink-400 italic">
            Login i role może zmienić tylko administrator (Administracja → Użytkownicy).
          </div>
        </div>
      </div>
    </div>
  );
}

/** Karta edycji profilu — imię, nazwisko, email. */
function ProfileEditCard() {
  const { user, refresh } = useAuth();
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    setFirstName(user?.firstName ?? '');
    setLastName(user?.lastName ?? '');
    setEmail(user?.email ?? '');
  }, [user]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      await authApi.updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || null,
      });
      setMsg({ type: 'ok', text: 'Profil zapisany.' });
      // Re-fetch /me żeby AuthContext miał aktualne dane (np. inicjały w sidebarze).
      try { await refresh?.(); } catch { /* nonfatal */ }
    } catch (e) {
      setMsg({ type: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  const dirty = firstName !== (user?.firstName ?? '')
             || lastName !== (user?.lastName ?? '')
             || (email || '') !== (user?.email ?? '');

  return (
    <div className="card mb-6">
      <div className="card-head">
        <div className="card-title">Profil — imię, nazwisko, email</div>
      </div>
      <div className="card-body">
        {msg && (
          <div className={'mb-3 p-2.5 rounded text-[13px] '
            + (msg.type === 'ok' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                  : 'bg-red-50 text-red-700 border border-red-200')}>
            {msg.text}
          </div>
        )}
        <form onSubmit={submit} className="grid grid-cols-2 gap-3 max-w-[600px]">
          <label className="field">
            <span className="field-label">Imię</span>
            <input value={firstName} onChange={e => setFirstName(e.target.value)}
                   className="input" required minLength={1}/>
          </label>
          <label className="field">
            <span className="field-label">Nazwisko</span>
            <input value={lastName} onChange={e => setLastName(e.target.value)}
                   className="input" required minLength={1}/>
          </label>
          <label className="field col-span-2">
            <span className="field-label">Email</span>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                   className="input" placeholder="np. jan.kowalski@hydrowolski.pl"/>
          </label>
          <div className="col-span-2">
            <button type="submit" disabled={busy || !dirty} className="btn btn-primary btn-sm">
              {busy ? 'Zapisuję…' : 'Zapisz zmiany'}
            </button>
            {dirty && !busy && (
              <span className="ml-2 text-[11px] text-amber-600">Niezapisane zmiany</span>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

/** Karta preferencji powiadomień — checkbox per kategoria × kanał (in-app, email). */
function NotificationPrefsCard() {
  const [prefs, setPrefs] = useState<NotificationPrefDto[] | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    notificationPrefs.list()
      .then(setPrefs)
      .catch(e => setErr(String(e)));
  }, []);

  async function toggle(category: string, channel: 'inApp' | 'email', value: boolean) {
    const key = `${category}:${channel}`;
    setSavingKey(key);
    setErr(null);
    try {
      const updated = await notificationPrefs.update(category, channel === 'inApp'
        ? { inAppEnabled: value }
        : { emailEnabled: value });
      setPrefs(prev => (prev ?? []).map(p => p.category === category ? updated : p));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingKey(null);
    }
  }

  async function resetCategory(category: string) {
    setSavingKey(category + ':reset');
    setErr(null);
    try {
      const r = await notificationPrefs.reset(category);
      // Po reset-cie aktualizujemy lokalny stan defaults-ami zwróconymi z backendu.
      setPrefs(prev => (prev ?? []).map(p => p.category === category
        ? { ...p, inAppEnabled: r.defaultInApp, emailEnabled: r.defaultEmail }
        : p));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <div className="card mb-6">
      <div className="card-head">
        <div className="card-title">Powiadomienia (alerty)</div>
        <span className="ml-2 text-[11px] text-ink-400">
          decydujesz które alerty otrzymujesz i jakim kanałem
        </span>
      </div>
      <div className="card-body !p-0">
        {err && <div className="p-3 text-red-700 bg-red-50 border-b border-red-200 text-[12px]">Błąd: {err}</div>}
        {!prefs && <div className="p-4 text-ink-400 text-[12px]">Ładowanie…</div>}
        {prefs && (
          <table className="table-hw">
            <thead>
              <tr>
                <th>Kategoria</th>
                <th className="text-center w-24">Wewnątrz aplikacji</th>
                <th className="text-center w-24">Email</th>
                <th className="text-center w-20">Reset</th>
              </tr>
            </thead>
            <tbody>
              {prefs.map(p => {
                const inAppKey = `${p.category}:inApp`;
                const emailKey = `${p.category}:email`;
                const resetKey = `${p.category}:reset`;
                const inAppCustom = p.inAppEnabled !== p.defaultInApp;
                const emailCustom = p.emailEnabled !== p.defaultEmail;
                const isCustom = inAppCustom || emailCustom;
                return (
                  <tr key={p.category}>
                    <td>
                      <div className="font-semibold text-ink-900 text-[13px]">{p.displayName}</div>
                      <div className="text-[11px] text-ink-500 mt-0.5 max-w-[600px]">{p.description}</div>
                      <div className="text-[10px] text-ink-400 mt-0.5 font-mono">
                        domyślne: in-app {p.defaultInApp ? '✓' : '✗'} · email {p.defaultEmail ? '✓' : '✗'}
                      </div>
                    </td>
                    <td className="text-center">
                      <label className="inline-flex items-center cursor-pointer"
                             title={inAppCustom ? 'Zmodyfikowane od domyślnego' : 'Wartość domyślna'}>
                        <input
                          type="checkbox"
                          checked={p.inAppEnabled}
                          disabled={savingKey === inAppKey}
                          onChange={e => toggle(p.category, 'inApp', e.target.checked)}
                          className="w-4 h-4"
                        />
                        {inAppCustom && <span className="ml-1.5 text-[10px] text-amber-600">●</span>}
                      </label>
                    </td>
                    <td className="text-center">
                      <label className="inline-flex items-center cursor-pointer"
                             title={emailCustom ? 'Zmodyfikowane od domyślnego' : 'Wartość domyślna'}>
                        <input
                          type="checkbox"
                          checked={p.emailEnabled}
                          disabled={savingKey === emailKey}
                          onChange={e => toggle(p.category, 'email', e.target.checked)}
                          className="w-4 h-4"
                        />
                        {emailCustom && <span className="ml-1.5 text-[10px] text-amber-600">●</span>}
                      </label>
                    </td>
                    <td className="text-center">
                      <button
                        onClick={() => resetCategory(p.category)}
                        disabled={!isCustom || savingKey === resetKey}
                        className="text-[11px] text-water-700 hover:underline disabled:opacity-40 disabled:no-underline"
                        title={isCustom ? 'Przywróć wartości domyślne' : 'Już wartości domyślne'}
                      >
                        ↺ reset
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="px-4 py-2 border-t border-ink-100 text-[10px] text-ink-400 italic">
          „Wewnątrz aplikacji" = task na liście zadań / alerty na dashboardzie. „Email" = wysyłka maila
          (wymaga skonfigurowanego SMTP w systemie). Pomarańczowa kropka oznacza że ustawiłeś inaczej niż domyślnie.
        </div>
      </div>
    </div>
  );
}

function ChangePasswordCard() {
  const { zmienPassword } = useAuth();
  const [stare, setStare] = useState('');
  const [updated, setNew] = useState('');
  const [updated2, setNew2] = useState('');
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (updated.length < 8) { setMsg({ type: 'err', text: 'Hasło musi mieć min 8 znaków' }); return; }
    if (updated !== updated2) { setMsg({ type: 'err', text: 'Hasła nie są zgodne' }); return; }
    setBusy(true);
    try {
      await zmienPassword(stare, updated);
      setMsg({ type: 'ok', text: 'Hasło zostało zmienione.' });
      setStare(''); setNew(''); setNew2('');
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message ?? 'Błąd' });
    } finally { setBusy(false); }
  }

  return (
    <div className="card mb-6">
      <div className="card-head"><div className="card-title">Zmiana hasła</div></div>
      <div className="card-body">
        {msg && (
          <div className={'mb-3 p-2.5 rounded text-[13px] ' +
            (msg.type === 'ok' ? 'bg-green-100 text-green-500' : 'bg-red-100 text-red-500')}>
            {msg.text}
          </div>
        )}
        <form onSubmit={submit} className="grid grid-cols-2 gap-3 max-w-[600px]">
          <label className="field">
            <span className="field-label">Aktualne hasło</span>
            <input type="password" value={stare} onChange={e => setStare(e.target.value)} className="input" required />
          </label>
          <div />
          <label className="field">
            <span className="field-label">Nowe hasło (min. 8 znaków)</span>
            <input type="password" value={updated} onChange={e => setNew(e.target.value)} className="input" required minLength={8} />
          </label>
          <label className="field">
            <span className="field-label">Powtórz nowe hasło</span>
            <input type="password" value={updated2} onChange={e => setNew2(e.target.value)} className="input" required minLength={8} />
          </label>
          <div>
            <button type="submit" disabled={busy} className="btn btn-primary btn-sm">
              {busy ? 'Zapisuję…' : 'Zmień hasło'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
